import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from '../api/ApiClientProvider';
import type { ApiError } from '../api/ApiError';
import { garageKeys } from './queryKeys';
import type { VehicleDetail, VehicleSummary, VinDecode } from './types';
import { unwrap } from './unwrap';

/**
 * Loads the signed-in user's vehicles.
 *
 * @returns The query result for the garage list.
 */
export function useGarage(): UseQueryResult<VehicleSummary[], ApiError> {
  const client = useApiClient();

  return useQuery<VehicleSummary[], ApiError>({
    queryKey: garageKeys.list(),
    queryFn: async () =>
      unwrap(await client.GET('/api/vehicles'), 'Could not load your garage'),
  });
}

/**
 * Loads one vehicle with everything attached to it.
 *
 * @param vehicleId - The vehicle to load.
 * @returns The query result for the vehicle.
 */
export function useVehicle(vehicleId: string): UseQueryResult<VehicleDetail, ApiError> {
  const client = useApiClient();

  return useQuery<VehicleDetail, ApiError>({
    queryKey: garageKeys.vehicle(vehicleId),
    queryFn: async () =>
      unwrap(
        await client.GET('/api/vehicles/{vehicleId}', { params: { path: { vehicleId } } }),
        'Could not load that vehicle',
      ),
  });
}

/**
 * Decodes a VIN.
 *
 * @param vin - The VIN to decode, or an empty string to stay idle.
 * @returns The query result for the decode.
 * @remarks
 * Disabled until the VIN is the full 17 characters. Firing on every keystroke would hammer a free
 * public service with input that cannot possibly decode. The result is cached indefinitely because
 * a VIN identifies one physical vehicle and its attributes never change.
 */
export function useVinDecode(vin: string): UseQueryResult<VinDecode, ApiError> {
  const client = useApiClient();

  return useQuery<VinDecode, ApiError>({
    queryKey: garageKeys.vin(vin),
    enabled: vin.length === 17,
    staleTime: Infinity,
    retry: false,
    queryFn: async () =>
      unwrap(
        await client.GET('/api/vin/{vin}', { params: { path: { vin } } }),
        'Could not reach the vehicle data service',
      ),
  });
}
